/* eslint-disable no-console */
import { db } from "@/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp
} from "firebase/firestore";
import encryptionService from "@/services/encryptionService";

const state = {
  // Order data
  orders: [],
  memberOrders: [],
  currentOrder: null,

  // Loading states
  loadingOrders: false,
  loadingMemberOrders: false,
  loadingCurrentOrder: false,
  processingOrder: false,

  // Real-time subscriptions
  ordersUnsubscribe: null,
  memberOrdersUnsubscribe: null,

  // Error state
  error: null
};

// Helper functions for order data encryption/decryption
const encryptOrderSensitiveData = orderData => {
  if (!orderData) return orderData;

  const fieldsToEncrypt = encryptionService.getEncryptionFields("order");
  return encryptionService.encryptObject(orderData, fieldsToEncrypt);
};

const decryptOrderSensitiveData = orderData => {
  if (!orderData) return orderData;

  const fieldsToDecrypt = encryptionService.getEncryptionFields("order");
  return encryptionService.decryptObject(orderData, fieldsToDecrypt);
};

const actions = {
  /**
   * Load all orders for the program
   */
  async loadOrders({ commit, getters }) {
    commit("setLoadingOrders", true);
    commit("setOrderError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const ordersRef = collection(programRef, "orders");
      const ordersQuery = query(ordersRef, orderBy("created", "desc"));
      const querySnapshot = await getDocs(ordersQuery);

      const orders = [];
      querySnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        const decryptedData = decryptOrderSensitiveData(data);

        orders.push({
          id: docSnapshot.id,
          ...decryptedData,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        });
      });

      commit("setOrders", orders);
      return orders;
    } catch (error) {
      console.error("Error loading orders:", error);
      commit("setOrderError", error.message);
      commit("setOrders", []);
      throw error;
    } finally {
      commit("setLoadingOrders", false);
    }
  },

  /**
   * Load orders for a specific member with real-time updates
   */
  async loadMemberOrders({ commit, getters, state }, memberId) {
    if (!memberId) {
      console.error("Member ID is required");
      return [];
    }

    // Cleanup existing subscription
    if (state.memberOrdersUnsubscribe) {
      state.memberOrdersUnsubscribe();
    }

    commit("setLoadingMemberOrders", true);
    commit("setOrderError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const ordersRef = collection(programRef, "orders");
      const ordersQuery = query(
        ordersRef,
        where("member._id", "==", memberId),
        orderBy("created", "desc")
      );

      const unsubscribe = onSnapshot(
        ordersQuery,
        querySnapshot => {
          const orders = [];
          querySnapshot.forEach(docSnapshot => {
            const data = docSnapshot.data();
            const decryptedData = decryptOrderSensitiveData(data);

            orders.push({
              id: docSnapshot.id,
              ...decryptedData,
              created: data.created?.toDate() || new Date(),
              updated: data.updated?.toDate() || new Date()
            });
          });

          commit("setMemberOrders", orders);
          console.log(`Loaded ${orders.length} orders for member ${memberId}`);
        },
        error => {
          console.error("Error in member orders listener:", error);
          commit("setOrderError", error.message);
        }
      );

      commit("setMemberOrdersUnsubscribe", unsubscribe);
      return true;
    } catch (error) {
      console.error("Error setting up member orders listener:", error);
      commit("setOrderError", error.message);
      throw error;
    } finally {
      commit("setLoadingMemberOrders", false);
    }
  },

  /**
   * Get order by ID
   */
  async getOrderById({ commit, getters }, orderId) {
    if (!orderId) {
      console.error("Order ID is required");
      return null;
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const orderRef = doc(collection(programRef, "orders"), orderId);
      const orderDoc = await getDoc(orderRef);

      if (orderDoc.exists()) {
        const data = orderDoc.data();
        const decryptedData = decryptOrderSensitiveData(data);

        return {
          id: orderDoc.id,
          ...decryptedData,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        };
      } else {
        console.warn(`Order with ID ${orderId} not found`);
        return null;
      }
    } catch (error) {
      console.error("Error getting order by ID:", error);
      commit("setOrderError", error.message);
      return null;
    }
  },

  /**
   * Load current order details
   */
  async loadCurrentOrder({ commit, dispatch }, orderId) {
    if (!orderId) {
      throw new Error("Order ID is required");
    }

    commit("setLoadingCurrentOrder", true);
    commit("setCurrentOrder", null);
    commit("setOrderError", null);

    try {
      const order = await dispatch("getOrderById", orderId);
      if (order) {
        commit("setCurrentOrder", order);
        return order;
      } else {
        throw new Error("Order not found");
      }
    } catch (error) {
      console.error("Error loading current order:", error);
      commit("setOrderError", error.message);
      throw error;
    } finally {
      commit("setLoadingCurrentOrder", false);
    }
  },

  /**
   * Create a new order
   */
  async createOrder({ commit, getters, dispatch }, payload) {
    if (!payload || !payload.member || !payload.cartValue) {
      throw new Error("Order data with member and cart value is required");
    }

    commit("setProcessingOrder", true);
    commit("setOrderError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const ordersRef = collection(programRef, "orders");

      const locale = getters.currentProgram?.translate?.locale || "en";

      // Prepare order data - encrypt sensitive fields before saving
      const orderInput = {
        ...payload,
        locale: locale,
        status: payload.status || "Pending",
        orderNumber: payload.orderNumber || `ORD-${Date.now()}`,
        totalAmount: payload.cartValue,
        currency: payload.currency || "points",
        created: serverTimestamp(),
        updated: serverTimestamp(),
        createdBy: payload.member._id || payload.member.id,
        updatedBy: payload.member._id || payload.member.id
      };

      // Encrypt sensitive order data
      const encryptedOrderData = encryptOrderSensitiveData(orderInput);

      // Create the order
      const newOrderRef = await addDoc(ordersRef, encryptedOrderData);

      // Create transaction for member points
      const transaction = {
        points: payload.cartValue,
        type: "Redeem",
        notes: payload.body || `Order ${orderInput.orderNumber}`,
        description: "Reward Order",
        memberId: payload.member._id || payload.member.id,
        orderId: newOrderRef.id
      };

      // Subtract member points
      await dispatch("subtractMemberPoints", transaction);

      // Create temporary order for immediate UI update (decrypted for local state)
      const tempOrder = {
        ...payload,
        id: newOrderRef.id,
        locale: locale,
        status: payload.status || "Pending",
        orderNumber: orderInput.orderNumber,
        totalAmount: payload.cartValue,
        currency: payload.currency || "points",
        created: new Date(),
        updated: new Date(),
        createdBy: payload.member._id || payload.member.id,
        updatedBy: payload.member._id || payload.member.id
      };

      commit("createOrder", tempOrder);
      dispatch("setSnackbar", "Order submitted successfully");

      return {
        success: true,
        orderId: newOrderRef.id,
        orderNumber: orderInput.orderNumber
      };
    } catch (error) {
      console.error("Error creating order:", error);
      commit("setOrderError", error.message);
      throw new Error("Error occurred when creating order");
    } finally {
      commit("setProcessingOrder", false);
    }
  },

  /**
   * Update an existing order
   */
  async updateOrder({ commit, getters, dispatch }, { id, updates }) {
    if (!id || !updates) {
      throw new Error("Order ID and updates are required");
    }

    commit("setOrderError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const orderRef = doc(collection(programRef, "orders"), id);

      // Verify order exists
      const orderDoc = await getDoc(orderRef);
      if (!orderDoc.exists()) {
        throw new Error("Order not found");
      }

      // Encrypt sensitive fields in updates before saving
      const encryptedUpdates = encryptOrderSensitiveData({
        ...updates,
        updated: serverTimestamp(),
        updatedBy: getters.currentMember?.id || "system"
      });

      await updateDoc(orderRef, encryptedUpdates);

      // Update local state with decrypted data
      const updatedOrder = {
        ...orderDoc.data(),
        ...updates,
        id,
        updated: new Date(),
        updatedBy: getters.currentMember?.id || "system"
      };

      commit("updateOrder", updatedOrder);
      dispatch("setSnackbar", "Order updated successfully");
      return updatedOrder;
    } catch (error) {
      console.error("Error updating order:", error);
      commit("setOrderError", error.message);
      throw error;
    }
  },

  /**
   * Delete an order
   */
  async deleteOrder({ commit, getters, dispatch }, orderId) {
    if (!orderId) {
      throw new Error("Order ID is required");
    }

    commit("setOrderError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const orderRef = doc(collection(programRef, "orders"), orderId);

      await deleteDoc(orderRef);

      commit("deleteOrder", orderId);
      dispatch("setSnackbar", "Order deleted successfully");
      return true;
    } catch (error) {
      console.error("Error deleting order:", error);
      commit("setOrderError", error.message);
      throw error;
    }
  },

  /**
   * Cancel an order
   */
  async cancelOrder({ dispatch }, orderId) {
    if (!orderId) {
      throw new Error("Order ID is required");
    }

    try {
      await dispatch("updateOrder", {
        id: orderId,
        updates: {
          status: "Cancelled",
          cancelledAt: new Date(),
          cancelledBy: "member"
        }
      });

      return true;
    } catch (error) {
      console.error("Error cancelling order:", error);
      throw error;
    }
  },

  /**
   * Set processing order state
   */
  setProcessingOrder({ commit }, payload) {
    commit("setProcessingOrder", Boolean(payload));
  },

  /**
   * Clear current order
   */
  clearCurrentOrder({ commit }) {
    commit("setCurrentOrder", null);
  },

  /**
   * Clear member orders
   */
  clearMemberOrders({ commit }) {
    commit("setMemberOrders", []);
  },

  /**
   * Clear error state
   */
  clearOrderError({ commit }) {
    commit("setOrderError", null);
  },

  /**
   * Cleanup subscriptions
   */
  cleanupOrdersListeners({ state, commit }) {
    if (state.ordersUnsubscribe) {
      state.ordersUnsubscribe();
      commit("setOrdersUnsubscribe", null);
    }

    if (state.memberOrdersUnsubscribe) {
      state.memberOrdersUnsubscribe();
      commit("setMemberOrdersUnsubscribe", null);
    }
  }
};

const mutations = {
  setOrders(state, payload) {
    state.orders = payload;
  },

  setMemberOrders(state, payload) {
    state.memberOrders = payload;
  },

  setCurrentOrder(state, payload) {
    state.currentOrder = payload;
  },

  setLoadingOrders(state, payload) {
    state.loadingOrders = Boolean(payload);
  },

  setLoadingMemberOrders(state, payload) {
    state.loadingMemberOrders = Boolean(payload);
  },

  setLoadingCurrentOrder(state, payload) {
    state.loadingCurrentOrder = Boolean(payload);
  },

  setProcessingOrder(state, payload) {
    state.processingOrder = Boolean(payload);
  },

  setOrderError(state, payload) {
    state.error = payload;
  },

  createOrder(state, payload) {
    state.orders = [payload, ...state.orders];
  },

  updateOrder(state, payload) {
    state.orders = state.orders.map(item => {
      if (item.id === payload.id) {
        return { ...item, ...payload };
      }
      return item;
    });

    // Update current order if it matches
    if (state.currentOrder && state.currentOrder.id === payload.id) {
      state.currentOrder = { ...state.currentOrder, ...payload };
    }
  },

  deleteOrder(state, orderId) {
    state.orders = state.orders.filter(item => item.id !== orderId);

    // Clear current order if it matches
    if (state.currentOrder && state.currentOrder.id === orderId) {
      state.currentOrder = null;
    }
  },

  setOrdersUnsubscribe(state, unsubscribe) {
    state.ordersUnsubscribe = unsubscribe;
  },

  setMemberOrdersUnsubscribe(state, unsubscribe) {
    state.memberOrdersUnsubscribe = unsubscribe;
  }
};

const getters = {
  /**
   * Get all orders
   */
  orders(state) {
    return state.orders;
  },

  /**
   * Get member orders
   */
  memberOrders(state) {
    return state.memberOrders;
  },

  /**
   * Get current order
   */
  currentOrder(state) {
    return state.currentOrder;
  },

  /**
   * Get order by ID
   */
  getOrderById: state => id => {
    return state.orders.find(order => order.id === id) || null;
  },

  /**
   * Get orders by status
   */
  getOrdersByStatus: state => status => {
    return state.orders.filter(order => order.status === status);
  },

  /**
   * Get orders by member ID
   */
  getOrdersByMemberId: state => memberId => {
    return state.orders.filter(
      order => order.member?._id === memberId || order.member?.id === memberId
    );
  },

  /**
   * Get pending orders
   */
  pendingOrders(state) {
    return state.orders.filter(order => order.status === "Pending");
  },

  /**
   * Get completed orders
   */
  completedOrders(state) {
    return state.orders.filter(order => order.status === "Completed");
  },

  /**
   * Get cancelled orders
   */
  cancelledOrders(state) {
    return state.orders.filter(order => order.status === "Cancelled");
  },

  /**
   * Get loading states
   */
  loadingOrders(state) {
    return state.loadingOrders;
  },

  loadingMemberOrders(state) {
    return state.loadingMemberOrders;
  },

  loadingCurrentOrder(state) {
    return state.loadingCurrentOrder;
  },

  processingOrder(state) {
    return state.processingOrder;
  },

  /**
   * Get error state
   */
  orderError(state) {
    return state.error;
  },

  /**
   * Check if any orders are loading
   */
  ordersIsLoading(state) {
    return (
      state.loadingOrders ||
      state.loadingMemberOrders ||
      state.loadingCurrentOrder ||
      state.processingOrder
    );
  },

  /**
   * Get orders count
   */
  ordersCount(state) {
    return state.orders.length;
  },

  /**
   * Get member orders count
   */
  memberOrdersCount(state) {
    return state.memberOrders.length;
  },

  /**
   * Get orders count by status
   */
  getOrdersCountByStatus: state => status => {
    return state.orders.filter(order => order.status === status).length;
  },

  /**
   * Get total order value
   */
  totalOrderValue(state) {
    return state.orders.reduce((total, order) => {
      return total + (order.totalAmount || order.cartValue || 0);
    }, 0);
  },

  /**
   * Get member orders total value
   */
  memberOrdersTotalValue(state) {
    return state.memberOrders.reduce((total, order) => {
      return total + (order.totalAmount || order.cartValue || 0);
    }, 0);
  },

  /**
   * Check if orders exist
   */
  hasOrders(state) {
    return state.orders.length > 0;
  },

  /**
   * Check if member orders exist
   */
  hasMemberOrders(state) {
    return state.memberOrders.length > 0;
  },

  /**
   * Get orders as options for dropdowns
   */
  ordersOptions(state) {
    return state.orders.map(order => ({
      text: `${order.orderNumber || order.id} - ${order.status}`,
      value: order.id
    }));
  },

  /**
   * Get recent orders (last 10)
   */
  recentOrders(state) {
    return state.orders.slice(0, 10);
  },

  /**
   * Get recent member orders (last 10)
   */
  recentMemberOrders(state) {
    return state.memberOrders.slice(0, 10);
  },

  /**
   * Check if real-time listeners are active
   */
  hasOrdersListener(state) {
    return !!state.ordersUnsubscribe;
  },

  hasMemberOrdersListener(state) {
    return !!state.memberOrdersUnsubscribe;
  },

  /**
   * Get order statistics
   */
  orderStatistics(state) {
    const total = state.orders.length;
    const pending = state.orders.filter(order => order.status === "Pending")
      .length;
    const completed = state.orders.filter(order => order.status === "Completed")
      .length;
    const cancelled = state.orders.filter(order => order.status === "Cancelled")
      .length;

    return {
      total,
      pending,
      completed,
      cancelled,
      totalValue: state.orders.reduce(
        (sum, order) => sum + (order.totalAmount || order.cartValue || 0),
        0
      )
    };
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
